import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, Heart, Users, Building2, Palmtree, Hammer,
  Wrench, Trophy, Music, Plane, PartyPopper,
  Briefcase, Castle, Flower2
} from 'lucide-react';

/**
 * RelationshipTypeBadge Component
 *
 * Visual badge showing relationship type with icon, color-coded by category.
 * Includes tooltip with description on hover.
 *
 * Categories:
 * - Household: Blue
 * - Real Estate: Green
 * - Community: Purple
 * - Professional: Orange
 */

// Relationship type configuration with icons, colors, and descriptions
const RELATIONSHIP_TYPE_CONFIG = {
  // Household & Living Arrangements (Blue)
  nuclear_family: {
    label: 'Family',
    icon: Home,
    color: 'blue',
    category: 'Household',
    description: 'Traditional family household management'
  },
  couples_partners: {
    label: 'Couple',
    icon: Heart,
    color: 'blue',
    category: 'Household',
    description: 'Partners managing shared living space'
  },
  roommates: {
    label: 'Roommates',
    icon: Users,
    color: 'blue',
    category: 'Household',
    description: 'Shared housing coordination'
  },
  tenant_landlord: {
    label: 'Property',
    icon: Building2,
    color: 'blue',
    category: 'Household',
    description: 'Landlord-tenant maintenance coordination'
  },
  vacation_home: {
    label: 'Vacation Home',
    icon: Palmtree,
    color: 'blue',
    category: 'Household',
    description: 'Multi-property and vacation home management'
  },

  // Real Estate & Construction (Green)
  builder_buyer: {
    label: 'New Construction',
    icon: Hammer,
    color: 'green',
    category: 'Real Estate',
    description: 'Home construction project management'
  },
  renovation_project: {
    label: 'Renovation',
    icon: Wrench,
    color: 'green',
    category: 'Real Estate',
    description: 'Major renovation project coordination'
  },

  // Community & Social Groups (Purple)
  sports_team: {
    label: 'Sports Team',
    icon: Trophy,
    color: 'purple',
    category: 'Community',
    description: 'Team logistics and coordination'
  },
  performance_group: {
    label: 'Performance Group',
    icon: Music,
    color: 'purple',
    category: 'Community',
    description: 'Theater, chorus, or band coordination'
  },
  group_trip: {
    label: 'Group Trip',
    icon: Plane,
    color: 'purple',
    category: 'Community',
    description: 'Multi-person travel coordination'
  },
  event_planning: {
    label: 'Event Planning',
    icon: PartyPopper,
    color: 'purple',
    category: 'Community',
    description: 'Wedding, reunion, or event coordination'
  },

  // Professional & Business (Orange)
  small_business: {
    label: 'Small Business',
    icon: Briefcase,
    color: 'orange',
    category: 'Professional',
    description: 'Daily business operations management'
  },
  hoa_community: {
    label: 'HOA',
    icon: Castle,
    color: 'orange',
    category: 'Professional',
    description: 'Community property management'
  },
  cooperative: {
    label: 'Co-op',
    icon: Flower2,
    color: 'orange',
    category: 'Professional',
    description: 'Shared resource management'
  }
};

// Color scheme mappings
const COLOR_SCHEMES = {
  blue: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-700 dark:text-blue-300',
    border: 'border-blue-300 dark:border-blue-700',
    icon: 'text-blue-600 dark:text-blue-400'
  },
  green: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-700 dark:text-green-300',
    border: 'border-green-300 dark:border-green-700',
    icon: 'text-green-600 dark:text-green-400'
  },
  purple: {
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    text: 'text-purple-700 dark:text-purple-300',
    border: 'border-purple-300 dark:border-purple-700',
    icon: 'text-purple-600 dark:text-purple-400'
  },
  orange: {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-700 dark:text-orange-300',
    border: 'border-orange-300 dark:border-orange-700',
    icon: 'text-orange-600 dark:text-orange-400'
  }
};

const RelationshipTypeBadge = ({
  relationshipType = 'nuclear_family',
  size = 'md',
  showTooltip = true,
  className = ''
}) => {
  const [showTooltipState, setShowTooltipState] = useState(false);

  // Get configuration for this relationship type
  const config = RELATIONSHIP_TYPE_CONFIG[relationshipType] || RELATIONSHIP_TYPE_CONFIG.nuclear_family;
  const colors = COLOR_SCHEMES[config.color];
  const Icon = config.icon;

  // Size configurations
  const sizeClasses = {
    sm: {
      container: 'px-2 py-1 text-xs gap-1',
      icon: 'w-3 h-3'
    },
    md: {
      container: 'px-3 py-1.5 text-sm gap-1.5',
      icon: 'w-4 h-4'
    },
    lg: {
      container: 'px-4 py-2 text-base gap-2',
      icon: 'w-5 h-5'
    }
  };

  const currentSize = sizeClasses[size] || sizeClasses.md;

  return (
    <div className="relative inline-block">
      <motion.div
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onMouseEnter={() => showTooltip && setShowTooltipState(true)}
        onMouseLeave={() => setShowTooltipState(false)}
        className={`
          inline-flex items-center rounded-full border
          ${colors.bg} ${colors.text} ${colors.border}
          ${currentSize.container}
          font-medium transition-all duration-200
          ${className}
        `}
      >
        <Icon className={`${currentSize.icon} ${colors.icon}`} />
        <span>{config.label}</span>
      </motion.div>

      {/* Tooltip */}
      <AnimatePresence>
        {showTooltip && showTooltipState && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64"
          >
            <div className="bg-gray-900 dark:bg-gray-800 text-white text-sm rounded-lg p-3 shadow-xl">
              <div className="font-semibold mb-1">{config.label}</div>
              <div className="text-gray-300 dark:text-gray-400 text-xs">
                {config.description}
              </div>
              <div className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                Category: {config.category}
              </div>
              {/* Arrow */}
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                <div className="border-8 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Export configuration for use in other components
export { RELATIONSHIP_TYPE_CONFIG, COLOR_SCHEMES };
export default RelationshipTypeBadge;
